"use client"
import { cn } from "@/lib/utils"
import React, { useState } from "react"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

type Props = {
  text: string
  maxLength?: number
  buttonClassName?: string
  className?: string
}

/**
 * Truncates text with a button to expand the full text
 */
export default function TruncateTextWithButton({
  text,
  maxLength = 100,
  buttonClassName = "",
  className = "",
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false)
  const shouldTruncate = text.length > maxLength

  const displayText = isExpanded ? text : text.slice(0, maxLength)
  const buttonText = isExpanded ? "View Less" : "View More"
  const toggleExpanded = () => setIsExpanded(!isExpanded)

  return (
    <p className={className}>
      {displayText}
      {shouldTruncate && !isExpanded && "..."}
      {shouldTruncate && (
        <button
          onClick={toggleExpanded}
          className={cn(
            "inline-block ml-1 text-primary-button",
            buttonClassName
          )}
        >
          {buttonText}
        </button>
      )}
    </p>
  )
}

/**
 * Truncates text with a tooltip to show the full text
 */
export const TruncateTextWithTooltip = ({
  text = "",
  startChars = 4,
  endChars = 4,
  className = "",
}) => {
  // If the text is shorter than or equal to startChars + endChars, just return the text
  if (!text || text.length <= startChars + endChars) {
    return <span className={className}>{text}</span>
  }

  // Otherwise, truncate the middle and add ellipsis
  const start = text.substring(0, startChars)
  const end = text.substring(text.length - endChars)
  const truncated = `${start}...${end}`

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`${className}`}>{truncated}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs break-all max-w-xs">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
